import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader, Modal } from "@mantine/core";
import { fetchAsync, postAsync } from "../services/ApiHandler";
import toast from "react-hot-toast";
import iconImageAdd from "../img/icon-image-add.png";
import { useNavigate } from "react-router-dom";
import DownloadIcon from "../assets/icon/download.png";

type PlanTier = "free" | "lite" | "standard" | "advanced";

type TemplateTaskDefinition = {
  templateType: string;
  displayName: string;
  modelName: string;
  iconImagePath: string;
  allowMultipleImages: boolean;
  monthlyFreeQuota?: number;
  monthlyFreeRemaining?: number;
  consumingPointByPlan: Record<string, number | null | undefined>;
};

type ExecuteTemplateTaskResponse = {
  code?: number;
  message?: string;
  templateTaskId?: string;
  timelineId?: string;
  modelName?: string;
  aspectRatio?: string;
  imageSize?: string;
  imagePath?: string;
};

type GeminiTimelineDetailImage = {
  imageBase64?: string;
  ImageBase64?: string;
  imageMimeType?: string;
  ImageMimeType?: string;
};

type GeminiTimelineDetailMessage = {
  role?: "user" | "model" | "system";
  Role?: "user" | "model" | "system";
  imageBase64?: string;
  ImageBase64?: string;
  imageMimeType?: string;
  ImageMimeType?: string;
  images?: GeminiTimelineDetailImage[];
  Images?: GeminiTimelineDetailImage[];
};

type GeminiTimelineDetailResponse = {
  code?: number;
  Code?: number;
  messages?: GeminiTimelineDetailMessage[];
  Messages?: GeminiTimelineDetailMessage[];
};

type Props = {
  currentPlanTier: PlanTier;
  isLoggedIn: boolean;
  onRequireLogin: () => void;
  onRefreshRemainPoint: () => void;
  onOverLimit: () => void;
  onCreditPurchase: () => void;
  onInFlightCountChange?: (count: number) => void;
  onCompleted?: (result?: { templateType: string; timelineId?: string }) => Promise<void> | void;
};

function resolvePublicAssetPath(path: string): string {
  if (!path) return "";
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${process.env.PUBLIC_URL ?? ""}${normalized}`;
}

export const TemplateTaskPanel: FC<Props> = ({
  currentPlanTier,
  isLoggedIn,
  onRequireLogin,
  onRefreshRemainPoint,
  onOverLimit,
  onCreditPurchase,
  onInFlightCountChange,
  onCompleted,
}) => {
  const navigate = useNavigate();
  const [definitions, setDefinitions] = useState<TemplateTaskDefinition[]>([]);
  const [loadingDefinitions, setLoadingDefinitions] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateTaskDefinition | undefined>(undefined);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [generatedImageSrc, setGeneratedImageSrc] = useState<string | undefined>(undefined);
  const [generatedTimelineId, setGeneratedTimelineId] = useState<string | undefined>(undefined);
  const [executing, setExecuting] = useState(false);
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [couponDialogOpened, setCouponDialogOpened] = useState(false);
  const [couponRemainingCount, setCouponRemainingCount] = useState(0);
  const [couponTemplateName, setCouponTemplateName] = useState("");
  const [insufficientDialogOpened, setInsufficientDialogOpened] = useState(false);
  const [bgRemovalEffectActive, setBgRemovalEffectActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const executeTokenRef = useRef(0);
  const inFlightCountRef = useRef(0);
  const bgRemovalEffectTimerRef = useRef<number | null>(null);

  function clearBgRemovalEffectTimer() {
    if (bgRemovalEffectTimerRef.current !== null) {
      window.clearTimeout(bgRemovalEffectTimerRef.current);
      bgRemovalEffectTimerRef.current = null;
    }
  }

  useEffect(() => {
    return () => {
      clearBgRemovalEffectTimer();
    };
  }, []);

  function updateInFlightCount(delta: number) {
    inFlightCountRef.current = Math.max(0, inFlightCountRef.current + delta);
    onInFlightCountChange?.(inFlightCountRef.current);
  }

  const loadDefinitions = useCallback(async (): Promise<TemplateTaskDefinition[]> => {
    setLoadingDefinitions(true);
    try {
      const list = await fetchAsync<TemplateTaskDefinition[]>("TemplateTask/GetDefinitions");
      const normalized = list ?? [];
      setDefinitions(normalized);
      return normalized;
    } catch {
      setDefinitions([]);
      return [];
    } finally {
      setLoadingDefinitions(false);
    }
  }, []);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  const sortedDefinitions = useMemo(() => {
    const order = [
      "ai-background-removal",
      "upscale-4k",
      "character-sheet",
      "old-photo-restoration",
      "ghost-mannequin",
      "flat-lay"
    ];
    const rank = new Map(order.map((key, index) => [key, index]));
    return [...definitions].sort((a, b) => {
      const ar = rank.get(a.templateType) ?? 999;
      const br = rank.get(b.templateType) ?? 999;
      return ar - br;
    });
  }, [definitions]);

  function getCurrentCost(template: TemplateTaskDefinition): number | undefined {
    const freeRemaining = template.monthlyFreeRemaining ?? 0;
    if (freeRemaining > 0) return 0;
    const value = template.consumingPointByPlan?.[currentPlanTier];
    if (value === null || value === undefined) return undefined;
    return value;
  }

  function closeModal() {
    executeTokenRef.current += 1;
    clearBgRemovalEffectTimer();
    setSelectedTemplate(undefined);
    setSelectedImages([]);
    setGeneratedImageSrc(undefined);
    setGeneratedTimelineId(undefined);
    setExecuting(false);
    setCheckingCoupon(false);
    setCouponDialogOpened(false);
    setCouponRemainingCount(0);
    setCouponTemplateName("");
    setInsufficientDialogOpened(false);
    setBgRemovalEffectActive(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  async function onPickImages(files: FileList | null) {
    if (!files || files.length === 0 || !selectedTemplate) return;

    const dataUrls = await Promise.all(
      Array.from(files).map((file) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result as string) ?? "");
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        })
      )
    );

    const filtered = dataUrls.filter((e) => e !== "");
    setSelectedImages((prev) => {
      if (!selectedTemplate.allowMultipleImages) {
        return filtered.slice(0, 1);
      }
      const merged = [...prev, ...filtered];
      return merged.slice(0, 14);
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function removeImage(index: number) {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  }

  async function executeTemplateTask(templateOverride?: TemplateTaskDefinition, checkedRemaining?: number) {
    const template = templateOverride ?? selectedTemplate;
    if (!template || executing) return;
    if (selectedImages.length === 0) {
      toast.error("画像を選択してください。");
      return;
    }
    if (!template.allowMultipleImages && selectedImages.length !== 1) {
      toast.error("このテンプレートは画像1枚のみ対応です。");
      return;
    }

    const executeToken = ++executeTokenRef.current;
    updateInFlightCount(1);
    setExecuting(true);
    try {
      const response = await postAsync<any>("TemplateTask/Execute", {
        templateType: template.templateType,
        imageBase64List: selectedImages,
        imageBase64: selectedImages[0],
      });
      const data = response as ExecuteTemplateTaskResponse;
      const code = data.code ?? response.code;
      const message = data.message ?? response.message ?? "";
      if (code !== 1) {
        const isNoFreeQuota = (checkedRemaining ?? 0) <= 0;
        const isPlanUnavailable = message.includes("現在のプランではこのテンプレートを利用できません");
        if (isNoFreeQuota && (code === -2 || isPlanUnavailable)) {
          setInsufficientDialogOpened(true);
          return;
        }
        if (code === -2) {
          onOverLimit();
          return;
        }
        toast.error(message || "生成に失敗しました。");
        return;
      }

      const imagePath = data.imagePath;
      if (!imagePath) {
        toast.error("生成結果の取得に失敗しました。");
        return;
      }
      const resolvedSrc = /^https?:\/\//i.test(imagePath)
        ? imagePath
        : (process.env.REACT_APP_IMAGE_URL ? `${process.env.REACT_APP_IMAGE_URL}/${imagePath}` : "");
      if (!resolvedSrc) {
        toast.error("生成結果の表示に失敗しました。");
        return;
      }

      onRefreshRemainPoint();
      await loadDefinitions();
      await onCompleted?.({ templateType: template.templateType, timelineId: data.timelineId });
      if (executeToken !== executeTokenRef.current) return;
      setGeneratedImageSrc(resolvedSrc);
      setGeneratedTimelineId(data.timelineId);
      if (template.templateType === "ai-background-removal") {
        clearBgRemovalEffectTimer();
        setBgRemovalEffectActive(true);
        bgRemovalEffectTimerRef.current = window.setTimeout(() => {
          setBgRemovalEffectActive(false);
          bgRemovalEffectTimerRef.current = null;
        }, 1200);
      } else {
        setBgRemovalEffectActive(false);
      }
    } catch {
      if (executeToken !== executeTokenRef.current) return;
      toast.error("生成に失敗しました。時間をおいて再度お試しください。");
    } finally {
      updateInFlightCount(-1);
      if (executeToken === executeTokenRef.current) {
        setExecuting(false);
      }
    }
  }

  function openTemplate(template: TemplateTaskDefinition) {
    if (!isLoggedIn) {
      onRequireLogin();
      return;
    }

    executeTokenRef.current += 1;
    setSelectedTemplate(template);
    setSelectedImages([]);
    setGeneratedImageSrc(undefined);
    setGeneratedTimelineId(undefined);
    setExecuting(false);
    setCheckingCoupon(false);
    setCouponDialogOpened(false);
    setCouponRemainingCount(0);
    setCouponTemplateName("");
    setInsufficientDialogOpened(false);
    setBgRemovalEffectActive(false);
  }

  async function onClickGenerateWithCouponCheck() {
    if (!selectedTemplate || executing || checkingCoupon) return;
    if (selectedImages.length === 0) {
      toast.error("画像を選択してください。");
      return;
    }
    if (!selectedTemplate.allowMultipleImages && selectedImages.length !== 1) {
      toast.error("このテンプレートは画像1枚のみ対応です。");
      return;
    }

    setCheckingCoupon(true);
    try {
      const latestDefinitions = await loadDefinitions();
      const latestTemplate = latestDefinitions.find((e) => e.templateType === selectedTemplate.templateType) ?? selectedTemplate;
      const remaining = latestTemplate.monthlyFreeRemaining ?? 0;
      const normalCost = latestTemplate.consumingPointByPlan?.[currentPlanTier];

      if (remaining > 0) {
        setSelectedTemplate(latestTemplate);
        setCouponTemplateName(latestTemplate.displayName);
        setCouponRemainingCount(remaining);
        setCouponDialogOpened(true);
        return;
      }

      if (normalCost === null || normalCost === undefined) {
        setInsufficientDialogOpened(true);
        return;
      }

      await executeTemplateTask(latestTemplate, remaining);
    } catch {
      toast.error("無料利用枠の確認に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setCheckingCoupon(false);
    }
  }

  function getExecuteButtonLabel(templateType: string): string {
    switch (templateType) {
      case "ai-background-removal":
        return "AI背景削除する";
      case "upscale-4k":
        return "高解像度化する";
      case "character-sheet":
        return "キャラクターシートを作成する";
      case "old-photo-restoration":
        return "古写真を復活させる";
      case "flat-lay":
        return "アイテムのフラットレイ画像を作成する";
      case "ghost-mannequin":
        return "ゴーストマネキンを作成する";
      default:
        return "生成する";
    }
  }

  async function onClickDownloadGeneratedImage() {
    if (!generatedImageSrc) return;
    try {
      let blob: Blob | undefined;
      if (generatedTimelineId) {
        const detail = await fetchAsync<GeminiTimelineDetailResponse>("Generation/GetGeminiTimelineDetail", {
          timelineId: generatedTimelineId,
          includeImageBase64: true
        });
        const messages = detail.messages ?? detail.Messages ?? [];
        const modelMessages = [...messages].reverse().filter((m) => (m.role ?? m.Role) === "model");
        for (const message of modelMessages) {
          const imageList = message.images ?? message.Images ?? [];
          const first = imageList[0];
          const b64 = first?.imageBase64 ?? first?.ImageBase64 ?? message.imageBase64 ?? message.ImageBase64;
          const mime = first?.imageMimeType ?? first?.ImageMimeType ?? message.imageMimeType ?? message.ImageMimeType ?? "image/png";
          if (b64) {
            const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
            blob = new Blob([bytes], { type: mime });
            break;
          }
        }
      }

      if (!blob) {
        const response = await fetch(generatedImageSrc);
        if (!response.ok) throw new Error("download failed");
        blob = await response.blob();
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      const ext = blob.type === "image/jpeg" ? "jpg" : blob.type === "image/webp" ? "webp" : "png";
      link.download = `template-task-${Date.now()}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("ダウンロードに失敗しました。時間をおいて再度お試しください。");
    }
  }

  function onClickUseAsReferenceImage() {
    if (!generatedImageSrc) return;
    closeModal();
    navigate("/image-generation", {
      state: {
        prefillReferenceImageUrl: generatedImageSrc
      }
    });
  }

  return (
    <>
      <div className="mt-3 px-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[16px] font-bold text-[#1B2256]">おすすめAI編集</div>
          {loadingDefinitions && <Loader size="xs" />}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {sortedDefinitions.map((template) => {
            return (
              <button
                key={template.templateType}
                type="button"
                onClick={() => openTemplate(template)}
                className="relative w-[260px] h-[124px] shrink-0 rounded-sm overflow-hidden text-left border border-[#D9DCEF] clickable"
              >
                <div className="absolute inset-0">
                  <img src={resolvePublicAssetPath(template.iconImagePath)} alt={template.displayName} className="w-full h-full object-cover" />
                </div>
                <div
                  className="absolute inset-x-0 bottom-0 px-2 py-1"
                  style={{ background: "linear-gradient(180deg, rgba(8,11,45,0) 0%, rgba(8,11,45,0.85) 75%, rgba(8,11,45,0.95) 100%)" }}
                >
                  <div className="text-white text-[16px] leading-[1.1] font-bold tracking-tight whitespace-nowrap overflow-hidden text-ellipsis">
                    {template.displayName}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <Modal
        opened={!!selectedTemplate}
        onClose={closeModal}
        centered
        size="lg"
        withCloseButton={false}
        padding={0}
        radius={0}
      >
        {selectedTemplate && (
          <div className="bg-[#F4F4F5] px-5 sm:px-6 pt-5 pb-3 min-h-[380px]">
            <div className="flex items-start justify-between">
              <div className="text-[18px] sm:text-[20px] leading-none font-bold text-[#2A2C6C]">
                {selectedTemplate.displayName}
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="w-6 h-6 flex items-center justify-center text-black clickable"
                aria-label="閉じる"
              >
                <span className="text-[22px] leading-[0.8]">×</span>
              </button>
            </div>

            {selectedTemplate.templateType === "character-sheet" && (
              <div className="mt-2 text-[12px] leading-[1.6] text-[#5E6078]">
                <div>・必ず同じ人物・キャラクターの写真をアップロードしてください</div>
                <div>・キャラクターの特徴を捉えやすい様々な角度の写真を用意下さい（5枚以上推奨）</div>
                <div>・バストアップ、全身などがわかる写真も用意してください。</div>
              </div>
            )}
            {(selectedTemplate.templateType === "flat-lay" || selectedTemplate.templateType === "ghost-mannequin") && (
              <div className="mt-2 text-[12px] leading-[1.6] text-[#5E6078]">
                <div>・必ず同じアイテムの写真をアップロードしてください</div>
                <div>・アイテムの特徴を捉えやすい様々な角度の写真を用意ください（5枚以上推奨）</div>
                <div>・タグやロゴは詳細がわかりやすい写真があるとパフォーマンスが向上します。</div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple={selectedTemplate.allowMultipleImages}
              className="hidden"
              onChange={(e) => onPickImages(e.target.files)}
            />

            {!generatedImageSrc && (
              <div
                role="button"
                tabIndex={0}
                onClick={() => !executing && fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !executing) {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                className="mt-6 rounded-[22px] border-2 border-dashed border-[#CFCFD4] min-h-[150px] clickable flex flex-col items-center justify-center px-3 py-4"
              >
                {selectedImages.length === 0 && (
                  <>
                    <img src={iconImageAdd} alt="画像追加" className="w-7 h-7" />
                    <div className="mt-3 text-[18px] font-bold text-[#B4B4B7] tracking-wide">
                      画像をアップロードしてください
                    </div>
                  </>
                )}

                {selectedImages.length > 0 && (
                  <div className="w-full flex flex-wrap justify-center gap-2">
                    {selectedImages.map((img, index) => (
                      <div key={`template-image-${index}`} className="relative border rounded-lg overflow-hidden bg-white h-[150px] sm:h-[170px] w-full sm:w-[48%] flex items-center justify-center">
                        <img src={img} alt={`selected-${index}`} className="w-full h-full object-contain" />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(index);
                          }}
                          className="absolute top-1 right-1 w-5 h-5 bg-white rounded-full border flex items-center justify-center"
                          disabled={executing}
                        >
                          <img src="/images/icon-close.svg" alt="remove" className="w-2 h-2" />
                        </button>
                      </div>
                    ))}
                    {selectedTemplate.allowMultipleImages && selectedImages.length < 14 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!executing) fileInputRef.current?.click();
                        }}
                        className="border-2 border-dashed border-[#CFCFD4] rounded-lg bg-white h-[150px] sm:h-[170px] w-full sm:w-[48%] flex items-center justify-center clickable"
                        disabled={executing}
                        aria-label="画像を追加"
                      >
                        <img src={iconImageAdd} alt="画像追加" className="w-6 h-6" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {generatedImageSrc && (
              <div className="mt-4">
                <div className="w-full h-[260px] sm:h-[320px] overflow-hidden flex items-center justify-center">
                  {selectedTemplate?.templateType === "ai-background-removal" && selectedImages.length > 0 && (
                    <div className="relative w-full h-full flex items-center justify-center">
                      <div className="absolute inset-0 template-bg-removal-result-bg" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                          src={generatedImageSrc}
                          alt="生成結果"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      {bgRemovalEffectActive && (
                        <div className="absolute inset-0 flex items-center justify-center template-bg-removal-before-layer">
                          <img
                            src={selectedImages[0]}
                            alt="元画像"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      )}
                      {bgRemovalEffectActive && (
                        <div className="absolute left-0 right-0 template-bg-removal-scanline pointer-events-none" />
                      )}
                    </div>
                  )}
                  {selectedTemplate?.templateType !== "ai-background-removal" && (
                    <img src={generatedImageSrc} alt="生成結果" className="max-w-full max-h-full object-contain" />
                  )}
                </div>
              </div>
            )}

            {!generatedImageSrc && (
              <div className="mt-7 flex items-center gap-3">
                <div className="min-w-[44px] flex items-center text-[13px] font-bold text-[#767C85]">
                  <img src="/images/icon/icons8-lightning.png" alt="cost" className="w-3 h-3 mr-1" />
                  <span>{getCurrentCost(selectedTemplate) ?? "-"}</span>
                </div>
                <button
                  type="button"
                  onClick={onClickGenerateWithCouponCheck}
                  className="flex-1 h-[46px] rounded-none text-[16px] font-bold inline-flex items-center justify-center gap-2"
                  style={{
                    background: selectedImages.length === 0 || executing || checkingCoupon ? "#D8D8DA" : "linear-gradient(91.51deg, #0000F3 0.07%, #F416BF 100.07%)",
                    color: selectedImages.length === 0 || executing || checkingCoupon ? "#F1F1F2" : "white"
                  }}
                  disabled={executing || checkingCoupon || selectedImages.length === 0}
                >
                  {(executing || checkingCoupon) && <Loader size="xs" color="#F1F1F2" />}
                  <span>{executing ? "処理中..." : (checkingCoupon ? "確認中..." : getExecuteButtonLabel(selectedTemplate.templateType))}</span>
                </button>
              </div>
            )}

            {generatedImageSrc && (
              <div className="mt-4">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClickDownloadGeneratedImage}
                    className="w-[40px] h-[40px] rounded-md border border-[#CBD1E8] bg-white clickable inline-flex items-center justify-center"
                    title="ダウンロード"
                    aria-label="ダウンロード"
                  >
                    <img src={DownloadIcon} alt="ダウンロード" className="w-4 h-4 object-contain" />
                  </button>
                  <button
                    type="button"
                    onClick={onClickUseAsReferenceImage}
                    className="w-[40px] h-[40px] rounded-md border border-[#CBD1E8] bg-white clickable inline-flex items-center justify-center"
                    title="参照画像として使う"
                    aria-label="参照画像として使う"
                  >
                    <img src={iconImageAdd} alt="参照画像として使う" className="w-4 h-4 object-contain" />
                  </button>
                </div>
                <div className="mt-2 text-center">
                  <a href="/generation-history" className="text-[12px] text-[#4C5CC7] underline">
                    生成履歴を確認する
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        opened={couponDialogOpened}
        onClose={() => {
          setCouponDialogOpened(false);
        }}
        centered
        size="sm"
        zIndex={430}
        title={<div className="text-[16px] font-bold text-[#2A2C6C]">マンスリー無料利用クーポン</div>}
      >
        {selectedTemplate?.iconImagePath && (
          <div className="mb-3 w-full rounded-md bg-white">
            <img
              src={resolvePublicAssetPath(selectedTemplate.iconImagePath)}
              alt={selectedTemplate.displayName}
              className="w-full h-auto block"
            />
          </div>
        )}
        <div className="text-[13px] leading-[1.8] text-[#4B4F67]">
          <span>{`${couponTemplateName}は毎月3回までクレジット消費なしでご利用いただけます。`}</span>
          <span className="font-bold text-[#1F3CFF] underline">{`今月は残り${couponRemainingCount}回ご利用が可能です！`}</span>
          <span>どうぞお楽しみください！</span>
        </div>
        <button
          type="button"
          className="mt-4 w-full h-[40px] rounded-md text-[14px] font-bold text-[#1F3CFF] bg-white border border-[#1F3CFF] inline-flex items-center justify-center gap-2"
          onClick={async () => {
            setCouponDialogOpened(false);
            await executeTemplateTask(undefined, couponRemainingCount);
          }}
          disabled={executing}
        >
          {executing && <Loader size="xs" color="#FFFFFF" />}
          <span>生成を続ける</span>
        </button>
      </Modal>

      <Modal
        opened={insufficientDialogOpened}
        onClose={() => setInsufficientDialogOpened(false)}
        centered
        size="sm"
        zIndex={440}
        title={<div className="text-[16px] font-bold text-[#2A2C6C]">クレジット数が足りません</div>}
      >
        <div className="text-[13px] leading-[1.8] text-[#4B4F67]">
          この機能を通常利用するためには「来月の無料枠解放をお待ちいただく」か「サブスクリプションプランをアップグレードする」あるいは「クレジット数を追加購入する」のいずれかが必要となります。
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            className="h-[40px] rounded-md text-[13px] font-bold text-white"
            style={{ background: "linear-gradient(91.51deg, #0000F3 0.07%, #F416BF 100.07%)" }}
            onClick={() => {
              setInsufficientDialogOpened(false);
              closeModal();
              onCreditPurchase();
            }}
          >
            追加購入はこちら
          </button>
          <button
            type="button"
            className="h-[40px] rounded-md text-[13px] font-bold border border-[#CBD1E8] text-[#2A2C6C] bg-white"
            onClick={() => {
              setInsufficientDialogOpened(false);
              closeModal();
              onOverLimit();
            }}
          >
            プランをアップグレードする
          </button>
        </div>
        <div className="mt-3 text-center">
          <a href="/plan" className="text-[12px] text-[#4C5CC7] underline">
            料金プランの確認はこちら
          </a>
        </div>
      </Modal>
    </>
  );
};
